import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Divider, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

//import constants
import { IS_MOBILE } from "../../constants/breakpoints";
import { LocaleContext } from "../../routing/LangRouter";
import BASE_URL from "../../constants/baseUrls";

//import general components
import MobileHeader from "../../components/common/mobile-header/mobile-header";
import BaseTitle from "../../components/common/base-title/base-title";
import BaseCarousel from "../../components/common/base-carousel/base-carousel";
import { BaseButton } from "../../components/common/base-button/base-button";

//import view components
import TourDescription from "../../components/view/tour-information/tour-description/tour-description";
import TourFlight from "../../components/view/tour-information/tour-flight/tour-flight";
import TourHotel from "../../components/view/tour-information/tour-hotel/tour-hotel";
import PriceCard from "../../components/view/tour-information/price-card/price-card";
import ReservationForm from "../../components/view/tour-information/reservation-form/reservation-form";

//import images and icons
import { ArrowForward } from "iconsax-react";
import { convertToJalali } from "../../utils/date";
import { scrollToPosition } from "../../utils/utility";

function TourInformation() {
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //sessionStorage variables
    const tourInformation = JSON.parse(sessionStorage.getItem("tourInformation"));

    //scroll to top
    useEffect(() => {
        scrollToPosition(0, 0);
    }, []);

    //back to tour list
    const handleGoBack = () => {
        navigate(`/${locale}/tour`, { replace: true });
    };

    useEffect(() => {
        if (!tourInformation) {
            handleGoBack();
        }
    }, []);

    return (
        <Container maxWidth="xl">
            <MobileHeader title={` ${tourInformation?.name}`} justifyContent="center" handleClick={handleGoBack} />
            <Grid className="tour-information" container item xs={12} rowGap={5} mt={2} mb={5}>
                <Grid container item xs={12} rowGap={3} justifyContent="space-between" alignItems="flex-start">
                    {!IS_MOBILE && (
                        <BaseTitle
                            title={`${t("about")}  ${tourInformation?.name}`}
                            dividerColor="1px solid var(--medium-gray-color)"
                            button={
                                <BaseButton
                                    text={t("backToList")}
                                    color="outline-dark"
                                    radius="radius-10"
                                    size="small"
                                    rightIcon={<ArrowForward size="16" color="var(--teal-color-18)" />}
                                    handleClick={handleGoBack}
                                />
                            }
                        />
                    )}

                    <TourDescription tourInformation={tourInformation} />

                    <Grid container item md={7} order={{ xs: 1, md: 2 }}>
                        <BaseCarousel
                            imageList={tourInformation?.extra_images}
                            dir="rtl"
                            height={IS_MOBILE ? "200px" : "450px"}
                            slidesPerView={1}
                            spaceBetween={10}
                            loop={true}
                            navigation={true}
                            pagination={true}
                            grid={1}
                            thumbsGallery={IS_MOBILE ? false : true}
                            baseUrl={BASE_URL}
                        />
                    </Grid>
                </Grid>

                <Grid container item xs={12} rowGap={3}>
                    <BaseTitle
                        title={t("tickets")}
                        dividerColor="1px solid var(--medium-gray-color)"
                        button={
                            <Grid container item xs={12} columnGap={2} justifyContent="space-between">
                                <span className="flight-item__departure-flight">
                                    {t("departureDate")}:{" "}
                                    {locale === "fa"
                                        ? convertToJalali(tourInformation?.depart_flight_begin)
                                        : tourInformation?.depart_flight_begin.split("T")[0]}
                                </span>
                                |
                                <span className="flight-item__destination-flight">
                                    {t("returnDate")}:{" "}
                                    {locale === "fa"
                                        ? convertToJalali(tourInformation?.return_flight_begin)
                                        : tourInformation?.return_flight_begin.split("T")[0]}
                                </span>
                            </Grid>
                        }
                    />

                    <Grid className="flight-item" container item xs={12} rowGap={2} p={{ xs: 2, md: 4 }}>
                        <TourFlight
                            title={t("departureFlight")}
                            flightClass={tourInformation?.depart_flight_class}
                            airline={tourInformation?.depart_airlines}
                            departure={tourInformation?.origin_airport}
                            destination={tourInformation?.destination_airport}
                            duration={tourInformation?.depart_flight_stops_time}
                            departureTime={tourInformation?.depart_flight_begin}
                            arrivedTime={tourInformation?.depart_flight_finish}
                        />
                        <Divider width="100%" />
                        <TourFlight
                            title={t("returnFlight")}
                            flightClass={tourInformation?.return_flight_class}
                            airline={tourInformation?.return_airlines}
                            departure={tourInformation?.destination_airport}
                            destination={tourInformation?.origin_airport}
                            duration={tourInformation?.return_flight_stops_time}
                            departureTime={tourInformation?.return_flight_begin}
                            arrivedTime={tourInformation?.return_flight_finish}
                        />
                    </Grid>
                </Grid>

                <Grid container item xs={12} rowGap={3} columnGap={3}>
                    <BaseTitle title={t("hotels")} dividerColor="1px solid var(--medium-gray-color)" />

                    {tourInformation?.accommodations?.map((hotelItem, index) => {
                        return (
                            <Grid key={hotelItem?.id} container item md={3.85}>
                                <TourHotel hotelItem={hotelItem} index={index + 1} />
                            </Grid>
                        );
                    })}
                </Grid>

                <Grid container item xs={12} rowGap={3} columnGap={3}>
                    <BaseTitle title={t("prices")} dividerColor="1px solid var(--medium-gray-color)" />

                    {tourInformation?.prices?.map(priceItem => {
                        return (
                            <Grid key={priceItem?.id} container item md={2.85}>
                                <PriceCard priceItem={priceItem} />
                            </Grid>
                        );
                    })}
                </Grid>

                <Grid container item xs={12}>
                    <ReservationForm tourId={tourInformation.id} />
                </Grid>
            </Grid>
        </Container>
    );
}

export default TourInformation;
